import { OjWebElement } from '@oracle/oraclejet-webdriver/elements';

/**
 * This is the base class for oj-c-labelled-link WebElement, and is generated from the
 * component's metadata. Do not modify these contents since they'll be replaced
 * during the next generation.
 * Put overrides into the WebElements's subclass, LabelledLinkWebElement.ts.
 */
export class LabelledLinkWebElementBase extends OjWebElement {
  /**
   * Gets the value of <code>columnSpan</code> property.
   * Specifies how many columns this component should span.
   * @return The value of <code>columnSpan</code> property.
   *
   */
  public getColumnSpan(): Promise<number> {
    return this.getProperty<number>('columnSpan');
  }

  /**
   * Gets the value of <code>containerReadonly</code> property.
   * Specifies whether an ancestor container, like oj-c-form-layout, is readonly.
   * @return The value of <code>containerReadonly</code> property.
   *
   */
  public getContainerReadonly(): Promise<boolean> {
    return this.getProperty<boolean>('containerReadonly');
  }

  /**
   * Gets the value of <code>href</code> property.
   * Sets the URL that the link points to.
   * @return The value of <code>href</code> property.
   *
   */
  public getHref(): Promise<string> {
    return this.getProperty<string>('href');
  }

  /**
   * Gets the value of <code>labelEdge</code> property.
   * Specifies how the label is positioned for the component
   * @return The value of <code>labelEdge</code> property.
   *
   */
  public getLabelEdge(): Promise<string> {
    return this.getProperty<string>('labelEdge');
  }

  /**
   * Gets the value of <code>labelHint</code> property.
   * Represents a hint for rendering a label on the component.
   * @return The value of <code>labelHint</code> property.
   *
   */
  public getLabelHint(): Promise<string> {
    return this.getProperty<string>('labelHint');
  }

  /**
   * Gets the value of <code>labelStartWidth</code> property.
   * The width of the label when labelEdge is 'start'.
   * @return The value of <code>labelStartWidth</code> property.
   *
   */
  public getLabelStartWidth(): Promise<number | string> {
    return this.getProperty<number | string>('labelStartWidth');
  }

  /**
   * Gets the value of <code>labelWrapping</code> property.
   * Should the labels wrap or truncate when there is not enough available space.
   * @return The value of <code>labelWrapping</code> property.
   * @deprecated Since 18.0.0. Label truncation for 'start' and 'top' aligned labels is no longer recommended by the Redwood Design System. The default for labelWrapping was 'wrap' and that is now the only suggested pattern by UX design for 'start' and 'top' aligned labels. 'inside' aligned labels are always truncated per UX design and are not affected by this property's value.
   */
  public getLabelWrapping(): Promise<string> {
    return this.getProperty<string>('labelWrapping');
  }

  /**
   * Gets the value of <code>lineClamp</code> property.
   * Truncates text at a specific number of lines and then displays an ellipsis (…) at the end of the last line. The parent of the element needs to have a specific width so that text starts overflowing and produces a truncation. The lineClamp property is ignored when the oj-c-labelled-link is in a oj-c-form-layout.
   * @return The value of <code>lineClamp</code> property.
   *
   */
  public getLineClamp(): Promise<number> {
    return this.getProperty<number>('lineClamp');
  }

  /**
   * Gets the value of <code>target</code> property.
   * Sets the target attribute of the link.
   * @return The value of <code>target</code> property.
   *
   */
  public getTarget(): Promise<string> {
    return this.getProperty<string>('target');
  }

  /**
   * Gets the value of <code>text</code> property.
   * Specifies the text that should appear in the field.
   * @return The value of <code>text</code> property.
   *
   */
  public getTextProperty(): Promise<string> {
    return this.getProperty<string>('text');
  }

  /**
   * Gets the value of <code>textAlign</code> property.
   * Specifies how the text is aligned within the text field
   * @return The value of <code>textAlign</code> property.
   *
   */
  public getTextAlign(): Promise<string> {
    return this.getProperty<string>('textAlign');
  }

  /**
   * Gets the value of <code>unsafe_labelledBy</code> property.
   *
   * @return The value of <code>unsafe_labelledBy</code> property.
   *
   */
  public getUnsafeLabelledBy(): Promise<string> {
    return this.getProperty<string>('unsafe_labelledBy');
  }

  /**
   * Gets the value of <code>userAssistanceDensity</code> property.
   * Specifies the density of the form component's user assistance presentation.
   * @return The value of <code>userAssistanceDensity</code> property.
   *
   */
  public getUserAssistanceDensity(): Promise<string> {
    return this.getProperty<string>('userAssistanceDensity');
  }

  /**
   * Gets the value of <code>truncation</code> property.
   * Specifies how the text should be truncated when it overflows. The parent of the element needs to have a specific width so that text starts overflowing and produces a truncation. Truncation should not be used when lineClamp is used. The default behavior is for end truncation to occur, except for mixed readonly links, then truncation is forced to none, which can cause the link to clip.
   * @return The value of <code>truncation</code> property.
   *
   */
  public getTruncation(): Promise<string> {
    return this.getProperty<string>('truncation');
  }
}
